'use client';

import { useCallback } from 'react';
import type { MotionValue } from 'framer-motion';
import type { PrismFpsSample } from '@/components/ui/Prism';
import {
  HERO_BACKGROUND_WEBGL_FAILURE_LABEL,
  type HeroBackgroundPrismConfig,
} from './Cherobackground.constants';
import { useCherobackgroundEntranceAnimation } from './useCherobackgroundEntranceAnimation';
import { useCherobackgroundMotion } from './useCherobackgroundMotion';
import {
  type PrismBackgroundMode,
  usePrismHealthGate,
} from './usePrismHealthGate';

interface UseCherobackgroundControllerInput {
  delay: number;
  entranceReady: boolean;
  onAnimationEvent?: (eventName: string) => void;
}

export interface CherobackgroundControllerState {
  backgroundMode: PrismBackgroundMode;
  glow: MotionValue<number>;
  handlePrismError: (error: unknown) => void;
  handlePrismFpsSample: (sample: PrismFpsSample) => void;
  isMobile: boolean;
  prismConfig: HeroBackgroundPrismConfig;
  scale: MotionValue<number>;
}

export function useCherobackgroundController({
  delay,
  entranceReady,
  onAnimationEvent,
}: UseCherobackgroundControllerInput): CherobackgroundControllerState {
  const motion = useCherobackgroundMotion();
  // The per-second rAF FPS aggregate drives the health gate on EVERY window - it is the
  // adaptive fallback that swaps the WebGL background for the static one when frame health
  // is poor, so it is wired straight from Prism to the gate with nothing in between.
  const {
    mode: backgroundMode,
    recordFpsSample,
    reportPrismFailure,
  } = usePrismHealthGate({ isEntranceComplete: motion.isEntranceComplete });

  useCherobackgroundEntranceAnimation({
    delay,
    entranceReady,
    entryProgress: motion.entryProgress,
    onAnimationEvent,
  });

  const handlePrismError = useCallback((error: unknown): void => {
    console.warn(HERO_BACKGROUND_WEBGL_FAILURE_LABEL, error);
    reportPrismFailure();
  }, [reportPrismFailure]);

  return {
    backgroundMode,
    glow: motion.glow,
    handlePrismError,
    handlePrismFpsSample: recordFpsSample,
    isMobile: motion.isMobile,
    prismConfig: motion.prismConfig,
    scale: motion.scale,
  };
}
